import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Rss, Plus, Trash2, RefreshCw, ExternalLink, Check, X, Clock, 
  Globe, ChevronDown, ChevronUp, Calendar, CalendarDays, Edit2, Search, Newspaper
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  getNewsSources, createNewsSource, updateNewsSource, deleteNewsSource,
  triggerCrawl, getCrawledArticles, approveArticle, rejectArticle,
  deleteNewsPost, getAllNewsPosts, markAdminNewsViewed
} from '../utils/api';

const AdminNewsPage = () => {
  const [sources, setSources] = useState([]);
  const [articles, setArticles] = useState([]);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crawling, setCrawling] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSource, setNewSource] = useState({ name: '', url: '', source_type: 'rss' });
  const [expandedSources, setExpandedSources] = useState(false);
  const [scheduleDate, setScheduleDate] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeletePost, setConfirmDeletePost] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      if (activeTab === 'published') {
        // Fetch all published news posts
        const postsData = await getAllNewsPosts();
        setPublishedPosts(postsData);
        setArticles([]);
      } else {
        const [sourcesData, articlesData] = await Promise.all([
          getNewsSources(),
          getCrawledArticles(activeTab)
        ]);
        setSources(sourcesData);
        setArticles(articlesData);
        // Reset the "new articles" badge AFTER loading (is_new flags already computed server-side)
        if (activeTab === 'pending') {
          try {
            await markAdminNewsViewed();
            window.dispatchEvent(new Event('admin-news-viewed'));
          } catch (e) {
            console.error('Failed to mark crawler viewed:', e);
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePublishedPost = async (post) => {
    if (deletingPostId) return;
    setDeletingPostId(post.id);
    try {
      await deleteNewsPost(post.id);
      toast.success('News post deleted');
      setPublishedPosts(publishedPosts.filter(p => p.id !== post.id));
      setConfirmDeletePost(null);
    } catch (error) {
      toast.error('Failed to delete news post');
    } finally {
      setDeletingPostId(null);
    }
  };

  const handleAddSource = async () => {
    if (!newSource.name || !newSource.url) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const created = await createNewsSource(newSource);
      setSources([...sources, created]);
      setNewSource({ name: '', url: '', source_type: 'rss' });
      setShowAddSource(false);
      toast.success('News source added');
    } catch (error) {
      toast.error('Failed to add source');
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Delete this news source?')) return;
    try {
      await deleteNewsSource(sourceId);
      setSources(sources.filter(s => s.id !== sourceId));
      toast.success('Source deleted');
    } catch (error) {
      toast.error('Failed to delete source');
    }
  };

  const handleToggleSource = async (source) => {
    try {
      await updateNewsSource(source.id, { active: !source.active });
      setSources(sources.map(s => s.id === source.id ? { ...s, active: !s.active } : s));
      toast.success(source.active ? 'Source disabled' : 'Source enabled');
    } catch (error) {
      toast.error('Failed to update source');
    }
  };

  const handleCrawlAll = async () => {
    setCrawling(true);
    try {
      const result = await triggerCrawl();
      toast.success(result.message);
      fetchData();
    } catch (error) {
      toast.error('Crawl failed');
    } finally {
      setCrawling(false);
    }
  };

  const handleApprove = async (articleId) => {
    if (approvingId) return; // Prevent double-click
    setApprovingId(articleId);
    try {
      const date = scheduleDate[articleId] || null;
      const result = await approveArticle(articleId, date);
      toast.success(result.message || 'Article approved', { duration: 6000 });
      setArticles(articles.filter(a => a.id !== articleId));
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to approve article';
      toast.error(message);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (articleId) => {
    try {
      await rejectArticle(articleId);
      toast.success('Article rejected');
      setArticles(articles.filter(a => a.id !== articleId));
    } catch (error) {
      toast.error('Failed to reject article');
    }
  };

  const handleDeleteApproved = async (article) => {
    try {
      // Delete the news post that was created from this article
      if (article.news_post_id) {
        await deleteNewsPost(article.news_post_id);
      }
      toast.success('News post deleted');
      setConfirmDelete(null);
      // Refresh articles list
      const articlesData = await getCrawledArticles(activeTab);
      setArticles(articlesData);
    } catch (error) {
      toast.error('Failed to delete news post');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">News Crawler</h1>
          <p className="text-sm sm:text-base text-slate-600">Manage news sources and review crawled articles</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Link to="/admin/calendar" className="w-full sm:w-auto">
            <Button variant="outline" className="w-full sm:w-auto" data-testid="open-content-calendar-btn">
              <CalendarDays className="h-4 w-4 mr-2" />
              Content Calendar
            </Button>
          </Link>
          <Button 
            onClick={handleCrawlAll} 
            disabled={crawling}
            className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${crawling ? 'animate-spin' : ''}`} />
            {crawling ? 'Crawling...' : 'Crawl All Sources'}
          </Button>
        </div>
      </div>

      {/* News Sources Section */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <button
          onClick={() => setExpandedSources(!expandedSources)}
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
        >
          <div className="flex items-center gap-3">
            <Rss className="h-5 w-5 text-orange-500" />
            <span className="font-semibold text-slate-900">News Sources ({sources.length})</span>
          </div>
          {expandedSources ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>

        {expandedSources && (
          <div className="border-t border-slate-200">
            <div className="p-3 sm:p-4 space-y-3">
              {sources.map(source => (
                <div key={source.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 rounded-xl gap-3">
                  <div className="flex items-start sm:items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 sm:mt-0 ${source.active ? 'bg-green-500' : 'bg-slate-300'}`} />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{source.name}</p>
                      <p className="text-xs text-slate-500 truncate">{source.url}</p>
                      <p className="text-xs text-slate-400">Last crawled: {formatDate(source.last_crawled)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <button
                      onClick={() => handleToggleSource(source)}
                      className={`px-3 py-1 text-xs rounded-full ${
                        source.active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      {source.active ? 'Active' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => handleDeleteSource(source.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Add Source Form */}
              {showAddSource ? (
                <div className="p-3 sm:p-4 border border-slate-200 rounded-xl space-y-3">
                  <input
                    type="text"
                    placeholder="Source name (e.g., CLL Society)"
                    value={newSource.name}
                    onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm sm:text-base"
                  />
                  <input
                    type="url"
                    placeholder="RSS Feed URL"
                    value={newSource.url}
                    onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm sm:text-base"
                  />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={handleAddSource} className="bg-primary w-full sm:w-auto">
                      Add Source
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddSource(false)} className="w-full sm:w-auto">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddSource(true)}
                  className="flex items-center justify-center sm:justify-start gap-2 px-4 py-3 text-primary hover:bg-primary/5 rounded-lg w-full"
                >
                  <Plus className="h-4 w-4" />
                  Add News Source
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Articles Section */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 overflow-x-auto">
          <div className="flex min-w-max sm:min-w-0">
            {['pending', 'scheduled', 'approved', 'rejected', 'published'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium capitalize whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-primary/5 text-primary border-b-2 border-primary'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {tab === 'scheduled' && <Clock className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />}
                {tab === 'published' && <Newspaper className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />}
                {tab === 'published' ? 'All Posts' : tab}
              </button>
            ))}
          </div>
        </div>

        {/* Published Posts Tab - Shows all news posts for deletion */}
        {activeTab === 'published' ? (
          <div className="divide-y divide-slate-100">
            {publishedPosts.length === 0 ? (
              <div className="p-6 sm:p-8 text-center text-slate-500">
                <Newspaper className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No published news posts</p>
              </div>
            ) : (
              publishedPosts.map(post => (
                <div key={post.id} className="p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    {post.featured_image && (
                      <img 
                        src={post.featured_image} 
                        alt="" 
                        className="w-full sm:w-24 h-48 sm:h-24 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 text-base sm:text-lg line-clamp-2">{post.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Slug: {post.slug}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Published: {new Date(post.published_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a 
                        href={`/news/${post.slug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => setConfirmDeletePost(post)}
                        className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
        <div className="divide-y divide-slate-100">
          {articles.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-slate-500">
              <Globe className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No {activeTab} articles</p>
              {activeTab === 'pending' && (
                <p className="text-sm mt-2">Click "Crawl All Sources" to fetch new articles</p>
              )}
              {activeTab === 'scheduled' && (
                <p className="text-sm mt-2">Approved articles with future publish dates will appear here</p>
              )}
            </div>
          ) : (
            articles.map(article => (
              <div key={article.id} className="p-4">
                {/* Mobile: Stack vertically, Desktop: Side by side */}
                <div className="flex flex-col sm:flex-row gap-4">
                  {article.image_url && (
                    <img 
                      src={article.image_url} 
                      alt="" 
                      className="w-full sm:w-24 h-48 sm:h-24 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    {article.is_new && activeTab === 'pending' && (
                      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-semibold mb-1" data-testid="admin-article-new-chip">
                        New
                      </span>
                    )}
                    <a 
                      href={article.original_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block group"
                    >
                      <h3 className="font-semibold text-slate-900 mb-1 text-base sm:text-lg group-hover:text-primary transition-colors">
                        {article.title}
                        <ExternalLink className="inline h-4 w-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                    </a>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-3">{article.summary}</p>
                    
                    {/* Source info - wrap on mobile */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Rss className="h-3 w-3" />
                        {article.source_name}
                      </span>
                      <a 
                        href={article.original_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Read Full Article
                      </a>
                      {article.published_date && (
                        <span title="Original publish date">{formatDate(article.published_date)}</span>
                      )}
                      {article.crawled_at && (
                        <span className="flex items-center gap-1" title="Date added to the app">
                          <Clock className="h-3 w-3" />
                          Added {formatDate(article.crawled_at)}
                        </span>
                      )}
                      {activeTab === 'scheduled' && article.scheduled_publish_date && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                          <Clock className="h-3 w-3" />
                          Publishes: {formatDate(article.scheduled_publish_date)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action row — full-width, aligned identically on every card */}
                {activeTab === 'pending' && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-3 pt-3 border-t border-slate-100" data-testid={`article-actions-${article.id}`}>
                    <div className="relative w-full sm:w-44">
                      <input
                        type="date"
                        value={scheduleDate[article.id] || ''}
                        onChange={(e) => setScheduleDate({ ...scheduleDate, [article.id]: e.target.value })}
                        className={`h-10 px-3 text-sm border border-slate-200 rounded-lg w-full ${!scheduleDate[article.id] ? 'text-transparent' : ''}`}
                        title="Schedule date (optional)"
                      />
                      {!scheduleDate[article.id] && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">
                          dd/mm/yyyy
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(article.id)}
                        disabled={approvingId === article.id}
                        className={`flex-1 sm:flex-none h-10 w-full sm:w-32 flex items-center justify-center gap-1 px-4 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 active:bg-green-700 ${approvingId === article.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {approvingId === article.id ? (
                          <>
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            Approve
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleReject(article.id)}
                        className="flex-1 sm:flex-none h-10 w-full sm:w-28 flex items-center justify-center gap-1 px-4 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 active:bg-red-700"
                      >
                        <X className="h-4 w-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                )}

                {/* Delete button for approved/scheduled articles */}
                {(activeTab === 'approved' || activeTab === 'scheduled') && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => setConfirmDelete(article)}
                      className="h-10 flex items-center gap-1 px-4 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 active:bg-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Post
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        )}
      </div>

      {/* Delete Confirmation Modal for Approved Articles */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-3">Delete News Post?</h3>
            <p className="text-slate-600 mb-2">Are you sure you want to delete:</p>
            <p className="font-medium text-slate-900 mb-4 line-clamp-2">"{confirmDelete.title}"</p>
            <p className="text-sm text-red-600 mb-4">This will remove the post from the News page and cannot be undone.</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setConfirmDelete(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteApproved(confirmDelete)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal for Published Posts */}
      {confirmDeletePost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-3">Delete News Post?</h3>
            <p className="text-slate-600 mb-2">Are you sure you want to delete:</p>
            <p className="font-medium text-slate-900 mb-4 line-clamp-2">"{confirmDeletePost.title}"</p>
            <p className="text-sm text-red-600 mb-4">This will permanently remove the post from the News page.</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setConfirmDeletePost(null)}
                className="flex-1"
                disabled={deletingPostId}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeletePublishedPost(confirmDeletePost)}
                disabled={deletingPostId}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deletingPostId ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNewsPage;
